import React, { useState } from "react";
import axios from "axios";
import { X, Loader2, Send, Plus, Reply, KeyRound, Megaphone } from "lucide-react";
import { getToken, formatApiError } from "@/lib/adminApi";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHdr = () => ({ Authorization: `Bearer ${getToken()}` });

export default function AdminModal({ modal, onClose, onSuccess }) {
  const t = modal.type;
  const title =
    t === "add"
      ? "Aggiungi iscritto"
      : t === "broadcast"
      ? "Componi broadcast"
      : t === "reply"
      ? "Rispondi al messaggio"
      : t === "pw"
      ? "Cambia password"
      : "";

  return (
    <div
      className="fixed inset-0 z-[60] bg-[#0B192C]/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="admin-modal-overlay"
    >
      <div
        className="bg-[#F9F8F6] border border-[#0B192C] w-full max-w-2xl my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E1DA]">
          <h3 className="font-serif-display text-xl text-[#0B192C]">{title}</h3>
          <button
            onClick={onClose}
            data-testid="admin-modal-close"
            className="w-8 h-8 flex items-center justify-center hover:bg-[#E5E1DA]"
          >
            <X size={16} />
          </button>
        </div>
        <div className="p-6">
          {t === "add" && <AddForm onSuccess={onSuccess} />}
          {t === "broadcast" && <BroadcastForm onSuccess={onSuccess} />}
          {t === "reply" && (
            <ReplyForm message={modal.payload} onSuccess={onSuccess} />
          )}
          {t === "pw" && <PasswordForm onSuccess={onSuccess} />}
        </div>
      </div>
    </div>
  );
}

// ---------- Add subscriber ----------

function AddForm({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await axios.post(
        `${API}/admin/subscribers/add`,
        { email: email.trim().toLowerCase(), name: name.trim() || null },
        { headers: authHdr() }
      );
      onSuccess();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <Field label="Email">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="add-sub-email"
          className="w-full px-3 py-2 border border-[#0B192C]/20 bg-white text-sm focus:outline-none focus:border-[#0B192C]"
          placeholder="cliente@example.com"
        />
      </Field>
      <Field label="Nome (opzionale)">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          data-testid="add-sub-name"
          className="w-full px-3 py-2 border border-[#0B192C]/20 bg-white text-sm focus:outline-none focus:border-[#0B192C]"
          placeholder="Mario Rossi"
        />
      </Field>
      {error && <p className="text-xs text-red-700">{error}</p>}
      <p className="text-xs text-[#3A4F6B] font-light">
        L'iscritto sarà aggiunto al DB locale e all'Audience Resend.
        Riceverà la welcome email + drip automatico.
      </p>
      <SubmitBtn loading={loading} testid="add-sub-submit" icon={Plus}>
        Aggiungi iscritto
      </SubmitBtn>
    </form>
  );
}

// ---------- Broadcast ----------

function BroadcastForm({ onSuccess }) {
  const [subject, setSubject] = useState("");
  const [html, setHtml] = useState("");
  const [testMode, setTestMode] = useState(true);
  const [testTo, setTestTo] = useState("metododimare@gmail.com");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!testMode && !window.confirm("Invio a TUTTI gli iscritti. Confermi?"))
      return;
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const body = { subject, html };
      if (testMode) body.test_to = testTo;
      const { data } = await axios.post(`${API}/admin/broadcast`, body, {
        headers: authHdr(),
      });
      setResult(data);
      if (!testMode) setTimeout(onSuccess, 2000);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <Field label="Soggetto">
        <input
          type="text"
          required
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          data-testid="broadcast-subject"
          className="w-full px-3 py-2 border border-[#0B192C]/20 bg-white text-sm focus:outline-none focus:border-[#0B192C]"
          placeholder="es. Nuovo capitolo gratis questa settimana"
        />
      </Field>
      <Field label="Corpo (HTML supportato)">
        <textarea
          required
          rows={9}
          value={html}
          onChange={(e) => setHtml(e.target.value)}
          data-testid="broadcast-html"
          className="w-full px-3 py-2 border border-[#0B192C]/20 bg-white text-sm font-mono focus:outline-none focus:border-[#0B192C]"
          placeholder={`<p>Ciao!</p>\n<p>Questa settimana...</p>\n<p><a href="https://metododimare.com">Leggi di più</a></p>`}
        />
      </Field>
      <div className="bg-[#F4E8D6]/40 border border-[#E5E1DA] p-4">
        <label className="flex items-center gap-3 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={testMode}
            onChange={(e) => setTestMode(e.target.checked)}
            data-testid="broadcast-test-mode"
            className="w-4 h-4"
          />
          <span>
            <strong>Modalità test</strong> — invia a una sola email per
            verificare prima di inviare a tutti gli iscritti
          </span>
        </label>
        {testMode && (
          <input
            type="email"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            data-testid="broadcast-test-to"
            className="mt-3 w-full px-3 py-2 border border-[#0B192C]/20 bg-white text-sm focus:outline-none focus:border-[#0B192C]"
          />
        )}
      </div>
      {error && <p className="text-xs text-red-700">{error}</p>}
      {result && (
        <div
          data-testid="broadcast-result"
          className="text-sm bg-[#D6E5EC]/40 border border-[#0E7C8F] p-3 text-[#0B192C]"
        >
          ✓ {result.test_mode ? "Test inviato" : "Broadcast inviato"} ·{" "}
          <strong>{result.sent}</strong> inviate · {result.failed} fallite ·{" "}
          {result.total_recipients} destinatari totali
        </div>
      )}
      <SubmitBtn
        loading={loading}
        testid="broadcast-submit"
        icon={testMode ? Send : Megaphone}
      >
        {testMode ? "Invia test" : "Invia a tutti gli iscritti"}
      </SubmitBtn>
    </form>
  );
}

// ---------- Reply to message ----------

function ReplyForm({ message, onSuccess }) {
  const [subject, setSubject] = useState(
    `Re: il tuo messaggio a Metodo di Mare`
  );
  const [body, setBody] = useState(`Ciao ${message?.name || ""},\n\n`);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await axios.post(
        `${API}/admin/reply`,
        { message_id: message.id, subject, body },
        { headers: authHdr() }
      );
      onSuccess();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="text-xs text-[#3A4F6B] bg-[#F9F8F6] border border-[#E5E1DA] p-3">
        <strong>A:</strong> {message?.name || ""}{" "}
        <span className="font-mono">&lt;{message?.email}&gt;</span>
        <p className="mt-2 border-l-2 border-[#E89B6C] pl-3 italic text-[#3A4F6B] whitespace-pre-wrap">
          {message?.message}
        </p>
      </div>
      <Field label="Soggetto">
        <input
          type="text"
          required
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          data-testid="reply-subject"
          className="w-full px-3 py-2 border border-[#0B192C]/20 bg-white text-sm focus:outline-none focus:border-[#0B192C]"
        />
      </Field>
      <Field label="Risposta">
        <textarea
          required
          rows={9}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          data-testid="reply-body"
          className="w-full px-3 py-2 border border-[#0B192C]/20 bg-white text-sm focus:outline-none focus:border-[#0B192C]"
        />
      </Field>
      {error && <p className="text-xs text-red-700">{error}</p>}
      <p className="text-xs text-[#3A4F6B] font-light">
        L'email parte da <code className="font-mono">info@metododimare.com</code>{" "}
        con reply-to <code className="font-mono">metododimare@gmail.com</code>.
      </p>
      <SubmitBtn loading={loading} testid="reply-submit" icon={Reply}>
        Invia risposta
      </SubmitBtn>
    </form>
  );
}

// ---------- Change password ----------

function PasswordForm({ onSuccess }) {
  const [current, setCurrent] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (newPw !== confirm) {
      setError("La conferma non corrisponde alla nuova password.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await axios.post(
        `${API}/admin/change-password`,
        { current_password: current, new_password: newPw },
        { headers: authHdr() }
      );
      alert("Password aggiornata. Per sicurezza ti verrà chiesto di fare login di nuovo al prossimo refresh.");
      onSuccess();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <Field label="Password attuale">
        <input
          type="password"
          required
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          data-testid="pw-current"
          className="w-full px-3 py-2 border border-[#0B192C]/20 bg-white text-sm focus:outline-none focus:border-[#0B192C]"
        />
      </Field>
      <Field label="Nuova password (min. 8 caratteri)">
        <input
          type="password"
          required
          minLength={8}
          value={newPw}
          onChange={(e) => setNewPw(e.target.value)}
          data-testid="pw-new"
          className="w-full px-3 py-2 border border-[#0B192C]/20 bg-white text-sm focus:outline-none focus:border-[#0B192C]"
        />
      </Field>
      <Field label="Conferma nuova password">
        <input
          type="password"
          required
          minLength={8}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          data-testid="pw-confirm"
          className="w-full px-3 py-2 border border-[#0B192C]/20 bg-white text-sm focus:outline-none focus:border-[#0B192C]"
        />
      </Field>
      {error && <p className="text-xs text-red-700">{error}</p>}
      <SubmitBtn loading={loading} testid="pw-submit" icon={KeyRound}>
        Cambia password
      </SubmitBtn>
    </form>
  );
}

// ---------- shared ----------

function Field({ label, children }) {
  return (
    <div>
      <label className="overline block mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function SubmitBtn({ loading, testid, icon: Icon, children }) {
  return (
    <button
      type="submit"
      disabled={loading}
      data-testid={testid}
      className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 bg-[#0B192C] text-[#F9F8F6] text-xs uppercase tracking-[0.2em] hover:bg-[#1E3557] transition-colors disabled:opacity-60"
    >
      {loading ? (
        <>
          <Loader2 size={14} className="animate-spin" /> Invio...
        </>
      ) : (
        <>
          {Icon && <Icon size={14} />}
          {children}
        </>
      )}
    </button>
  );
}
